import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import RelatorioApiService from '../../../services/api/RelatorioApiService';
import { useLocation, useHistory } from 'react-router-dom';
import FormatDataService from '../../../services/util/FormatData';
import { MdOutlineArrowBack, MdFileDownload } from 'react-icons/md';
import PageContainer from '../../../components/PageContainer';
import { Alert, Button, Table, Dropdown } from 'react-bootstrap';
import Loading from '../../../components/Loading';
import UtilService from '../../../services/util';
import useAlertNotification from '../../../context/AlertNotificationContext';

function RelatorioHorasExtrasView() {
    const location = useLocation();
    const history = useHistory();
    const alertNotification = useAlertNotification();
    const { data } = location.state || { data: { periodoInicial: "", periodoFinal: "" } };
    const { periodoInicial, periodoFinal } = data;

    const [horasExtrasData, setHorasExtrasData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [isExporting, setIsExporting] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedRegime, setSelectedRegime] = useState('');
    const notificationShownRef = useRef(false);
    const previousFiltersRef = useRef('');

    /**
     * Função para converter string de tempo (HH:MM) para minutos
     * @param {string} timeString - String no formato HH:MM
     * @returns {number} - Total de minutos
     */
    const timeStringToMinutes = useCallback((timeString) => {
        if (!timeString || typeof timeString !== 'string') return 0;
        
        const parts = timeString.split(':');
        if (parts.length !== 2) return 0;
        
        const hours = parseInt(parts[0], 10) || 0;
        const minutes = parseInt(parts[1], 10) || 0;
        
        return hours * 60 + minutes;
    }, []);

    /**
     * Função para buscar os dados do relatório de horas extras
     */
    const fetchHorasExtrasData = useCallback(async () => {
        if (!periodoInicial || !periodoFinal) {
            setError('Período inicial e final são obrigatórios');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const dataInicialFormatada = FormatDataService.formatarDataParaYYYYMMDD(periodoInicial);
            const dataFinalFormatada = FormatDataService.formatarDataParaYYYYMMDD(periodoFinal);
            const response = await RelatorioApiService.getRelatorioHorasExtras(dataInicialFormatada, dataFinalFormatada);

            const newData = response.data.data || [];
            setHorasExtrasData(newData);
            // Reset da flag de notificação quando novos dados são carregados
            notificationShownRef.current = false;
            previousFiltersRef.current = '';
        } catch (error) {
            console.error('Erro ao carregar dados do relatório:', error);
            setError('Erro ao carregar dados do relatório. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }, [periodoInicial, periodoFinal]);

    useEffect(() => {
        fetchHorasExtrasData();
    }, [fetchHorasExtrasData]);

    // Notificação quando há filtros selecionados mas não há resultados
    useEffect(() => {
        const hasFilters = selectedDepartment || selectedRegime;
        const currentFilters = `${selectedDepartment}-${selectedRegime}`;
        
        // Reset da flag quando os filtros mudarem
        if (previousFiltersRef.current !== currentFilters) {
            notificationShownRef.current = false;
            previousFiltersRef.current = currentFilters;
        }
        
        // Mostra notificação quando há filtros selecionados mas não há resultados filtrados
        if (hasFilters && filteredData.length === 0 && horasExtrasData.length > 0 && !loading && !notificationShownRef.current) {
            alertNotification.info('Nenhum dado encontrado com os filtros selecionados.');
            notificationShownRef.current = true;
        }
    }, [selectedDepartment, selectedRegime, filteredData, horasExtrasData, loading, alertNotification]);

    // Filtros por departamento e regime
    useEffect(() => {
        // Se não há filtros selecionados, mostra todos os dados
        if (!selectedDepartment && !selectedRegime) {
            setFilteredData(horasExtrasData);
            return;
        }

        const filtered = horasExtrasData.filter((item) => {
            // Verifica se o item tem estrutura aninhada (colaborador.departamento) ou direta (departamento)
            const departamento = item.departamento || item.colaborador?.departamento?.nome || item.colaborador?.departamento;
            const regime = item.regime || item.colaborador?.regime?.nome || item.colaborador?.regime;

            // Normaliza os valores para comparação
            const departamentoNormalizado = typeof departamento === 'string' ? departamento : '';
            const regimeNormalizado = typeof regime === 'string' ? regime : '';

            // Filtro por departamento
            const matchesDepartment = selectedDepartment
                ? departamentoNormalizado.toLowerCase() === selectedDepartment.toLowerCase() ||
                (selectedDepartment === 'Delivery' && (departamentoNormalizado.toLowerCase().includes('delivery') || item.departamento?.cod === 5 || item.colaborador?.departamento?.cod === 5)) ||
                (selectedDepartment === 'ENGESOL' && (departamentoNormalizado.toLowerCase().includes('engesol') || item.departamento?.cod === 16 || item.colaborador?.departamento?.cod === 16)) ||
                (selectedDepartment === 'Overhead' && (departamentoNormalizado.toLowerCase().includes('overhead') || departamentoNormalizado.toLowerCase().includes('over')))
                : true;

            // Filtro por regime
            const matchesRegime = selectedRegime
                ? regimeNormalizado.toLowerCase() === selectedRegime.toLowerCase() ||
                (selectedRegime === 'CLT' && (regimeNormalizado.toLowerCase().includes('clt') || item.regime?.cod === 1 || item.colaborador?.regime?.cod === 1)) ||
                (selectedRegime === 'Estagiário' && (regimeNormalizado.toLowerCase().includes('estagiário') || regimeNormalizado.toLowerCase().includes('estagiario') || item.regime?.cod === 3 || item.colaborador?.regime?.cod === 3))
                : true;

            return matchesDepartment && matchesRegime;
        });
        setFilteredData(filtered);
    }, [selectedDepartment, selectedRegime, horasExtrasData]); 

    /**
     * Função para ordenar os dados
     * @param {string} key - Chave da coluna para ordenar
     */
    const handleSort = useCallback((key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    }, [sortConfig]);

    /**
    * Dados ordenados baseados na configuração de ordenação
    */
    const sortedData = useMemo(() => {
        // Se há filtros selecionados, sempre usa filteredData (mesmo que vazio)
        // Se não há filtros, usa todos os dados
        const hasFilters = selectedDepartment || selectedRegime;
        const dataToSort = hasFilters ? filteredData : (filteredData.length > 0 ? filteredData : horasExtrasData);
        if (!sortConfig.key) return dataToSort;

        return [...dataToSort].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // Para colunas de tempo, converte para minutos para ordenação
            if (sortConfig.key.includes('horas') || sortConfig.key === 'diferenca') {
                aValue = timeStringToMinutes(aValue);
                bValue = timeStringToMinutes(bValue);
            }

            // Para strings, converte para minúsculo para ordenação case-insensitive
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredData, horasExtrasData, sortConfig, selectedDepartment, selectedRegime]);

    const tableRows = useMemo(() =>
        sortedData.map((item, index) => (
            <tr key={item.id || `item-${index}`}>
                <td>{item.nomeCompleto || '-'}</td>
                <td>{item.gestor || '-'}</td>
                <td>{item.horaExtras || '-'}</td>
                <td>{item.email || '-'}</td>
            </tr>
        )),
        [sortedData]
    );

    const exportDataInExcel = useCallback(async () => {
        setIsExporting(true);
        
        try {
            const header = [
                ['Nome', 'Gestor', 'Horas Extras', 'E-mail']
            ];

            const formattedData = sortedData.map(item => [
                item.nomeCompleto || '',
                item.gestor || '',
                item.horaExtras || '',
                item.email || ''
            ]);

            const nomeArquivo = `Relatório de Horas Extras - ${new Date(periodoInicial).toLocaleDateString('pt-BR')} a ${new Date(periodoFinal).toLocaleDateString('pt-BR')}`;
            await UtilService.exportXls(formattedData, nomeArquivo, header);
        } catch (error) {
            console.error('Erro ao exportar dados:', error);
            setError('Erro ao exportar dados. Tente novamente.');
        } finally {
            setIsExporting(false);
        }
    }, [sortedData, periodoInicial, periodoFinal]);

    const actions = useMemo(
        () => [
            {
                label: "Voltar",
                icon: <MdOutlineArrowBack />,
                color: "info",
                type: "button",
                onClick: () => history.goBack()
            }
        ],
        [history]
    );
    const title = useMemo(() =>
        `Relatório de Horas Extras - ${new Date(periodoInicial || new Date()).toLocaleDateString('pt-BR')} a ${new Date(periodoFinal || new Date()).toLocaleDateString('pt-BR')}`,
        [periodoInicial, periodoFinal]
    );

    return (
        <PageContainer title={title} actions={actions}>
            {loading ? <Loading message="Carregando..." /> : (
                <>
                {error && (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                )}
                {!error && horasExtrasData.length === 0 && (
                    <Alert variant="warning">
                        Nenhum dado disponível para o período selecionado.
                    </Alert>
                )}

                {!error && horasExtrasData.length > 0 && (
                    <>
                        <div className="d-flex mb-3">
                            <Button 
                                onClick={exportDataInExcel} 
                                className="mr-3"
                                disabled={isExporting || sortedData.length === 0}
                            >
                                Exportar
                            </Button>
                            <Dropdown className="mr-3">
                                <Dropdown.Toggle variant="light" id="dd-department">
                                    Filtrar por Departamento: <span style={{ fontWeight: 'bold' }}>{selectedDepartment ? selectedDepartment : "Todos"}</span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => setSelectedDepartment('Delivery')}>
                                        Delivery
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setSelectedDepartment('ENGESOL')}>
                                        ENGESOL
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setSelectedDepartment('Overhead')}>
                                        Overhead
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setSelectedDepartment('')}>
                                        Todos
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                            <Dropdown>
                                <Dropdown.Toggle variant="light" id="dd-regime">
                                    Filtrar por Regime: <span style={{ fontWeight: 'bold' }}>{selectedRegime ? selectedRegime : "Todos"}</span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => setSelectedRegime('CLT')}>
                                        CLT
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setSelectedRegime('Estagiário')}>
                                        Estagiário
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setSelectedRegime('')}>
                                        Todos
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* Mostra mensagem quando há filtros mas não há resultados */}
                        {(selectedDepartment || selectedRegime) && sortedData.length === 0 && (
                            <Alert variant="info">
                                Nenhum dado encontrado com os filtros selecionados.
                            </Alert>
                        )}

                        {/* Mostra tabela apenas quando há resultados */}
                        {sortedData.length > 0 && (
                            <div className="table-responsive container-fluid p-0">
                    <Table 
                        striped 
                        bordered 
                        hover 
                        responsive 
                        aria-label="Relatório de Horas Extras"
                        role="table"
                    >
                        <thead>
                            <tr>
                                <th
                                    scope="col"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleSort('nomeCompleto')}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleSort('nomeCompleto');
                                        }
                                    }}
                                    aria-label={`Ordenar por Nome ${sortConfig.key === 'nomeCompleto' ? (sortConfig.direction === 'asc' ? 'crescente' : 'decrescente') : ''}`}
                                    role="button"
                                >
                                    Nome
                                    {sortConfig.key === 'nomeCompleto' && (
                                        <span aria-hidden="true">
                                            {sortConfig.direction === 'asc' ? ' ▴' : ' ▾'}
                                        </span>
                                    )}
                                </th>
                                <th
                                    scope="col"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleSort('gestor')}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleSort('gestor');
                                        }
                                    }}
                                    aria-label={`Ordenar por Gestor ${sortConfig.key === 'gestor' ? (sortConfig.direction === 'asc' ? 'crescente' : 'decrescente') : ''}`}
                                    role="button"
                                >
                                    Gestor
                                    {sortConfig.key === 'gestor' && (
                                        <span aria-hidden="true">
                                            {sortConfig.direction === 'asc' ? ' ▴' : ' ▾'}
                                        </span>
                                    )}
                                </th>
                                <th
                                    scope="col"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleSort('horaExtras')}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleSort('horaExtras');
                                        }
                                    }}
                                    aria-label={`Ordenar por Horas Extras ${sortConfig.key === 'horaExtras' ? (sortConfig.direction === 'asc' ? 'crescente' : 'decrescente') : ''}`}
                                    role="button"
                                >
                                    Horas Extras
                                    {sortConfig.key === 'horaExtras' && (
                                        <span aria-hidden="true">
                                            {sortConfig.direction === 'asc' ? ' ▴' : ' ▾'}
                                        </span>
                                    )}
                                </th>
                                <th
                                    scope="col"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleSort('email')}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleSort('email');
                                        }
                                    }}
                                    aria-label={`Ordenar por E-mail ${sortConfig.key === 'email' ? (sortConfig.direction === 'asc' ? 'crescente' : 'decrescente') : ''}`}
                                    role="button"
                                >
                                    E-mail
                                    {sortConfig.key === 'email' && (
                                        <span aria-hidden="true">
                                            {sortConfig.direction === 'asc' ? ' ▴' : ' ▾'}
                                        </span>
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableRows.length > 0 ? tableRows : (
                                <tr>
                                    <td colSpan="4" className="text-center text-muted py-4">
                                        Nenhum dado encontrado para o período selecionado
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                            </div>
                        )}
                    </>
                )}
            </>
            )}
        </PageContainer>
    )
}

export default RelatorioHorasExtrasView;